/**
 * WallModeToggle (v1.1 — wall-tablet opt-in).
 *
 * Per-device control mounted in the /settings route.  Switches THIS device
 * into the kitchen wall-tablet display: the ambient idle hub (clock, weather,
 * calendar) becomes the cold-boot surface and the 60s auto-idle timer kicks
 * in.  Off by default, so every phone and laptop opens straight into the
 * interactive app for whoever is logged in.
 *
 * The choice is persisted in localStorage by the dashboard store
 * (`enableWallMode` / `disableWallMode`), so a configured tablet survives
 * reloads.  Because it's a device-local preference — not a server setting —
 * it lives in plain settings, available to any logged-in member, and never
 * touches the API.
 *
 * #152: av/på er Switch-primitiven, med tittelen som synlig etikett. De to
 * gamle knappene var «slå på» og «slå av» for samme valg; testid-ene deres
 * sitter nå på bryteren og følger det den gjør ved trykk
 * (`wall-mode-enable` når den er av, `wall-mode-disable` når den er på).
 *
 * #159 (steg 9d, Stell_Meg): en rad i et glasskort — «Veggmodus» med
 * forklaringen under og bryteren til høyre. Forklaringen har testid-en til
 * den gamle statuslinjen; av/på står på bryteren (`aria-checked`).
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../../components/primitives/Switch';
import { ADMIN_LIST } from '../admin/adminStyles';
import {
  useDashboard,
  useWallMode,
} from '../dashboard/DashboardStore';

export function WallModeToggle(): ReactElement {
  const { t } = useTranslation();
  const wallMode = useWallMode();
  const enableWallMode = useDashboard((s) => s.enableWallMode);
  const disableWallMode = useDashboard((s) => s.disableWallMode);

  return (
    <section
      data-testid="wall-mode-toggle"
      className={`${ADMIN_LIST} pr-2`}
      aria-labelledby="wall-mode-title"
    >
      <div className="flex min-h-15 items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 id="wall-mode-title" className="m-0 text-body-s font-extrabold text-ink">
            {t('settings.wall_mode.title')}
          </h2>
          <p data-testid="wall-mode-status" className="m-0 text-caption text-ink-2">
            {t('settings.wall_mode.body')}
          </p>
        </div>
        <Switch
          checked={wallMode}
          labelledBy="wall-mode-title"
          onChange={(on) => (on ? enableWallMode() : disableWallMode())}
          data-testid={wallMode ? 'wall-mode-disable' : 'wall-mode-enable'}
        />
      </div>
    </section>
  );
}
